import { useState, useRef, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Search, Sun, Moon, Bell, LogOut, FileText, Video, User } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { useAuth } from '@/hooks/useAuth'
import { useNotifications } from '@/hooks/useNotifications'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'
import { timeAgo } from '@/lib/utils'

export default function Header() {
  const { theme, toggle } = useTheme()
  const { user } = useAuth()
  const { notifications, unreadCount, markAllRead, markRead } = useNotifications()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [query, setQuery] = useState(searchParams.get('q') ?? '')
  const [showNotif, setShowNotif] = useState(false)
  const [showUser, setShowUser] = useState(false)
  const notifRef = useRef<HTMLDivElement>(null)
  const userRef = useRef<HTMLDivElement>(null)

  const initials = (user?.email ?? 'AD').slice(0, 2).toUpperCase()

  // Close dropdowns on outside click
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setShowNotif(false)
      if (userRef.current && !userRef.current.contains(e.target as Node)) setShowUser(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [])

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    if (query.trim()) navigate(`/app/search?q=${encodeURIComponent(query.trim())}`)
  }

  async function handleSignOut() {
    await supabase.auth.signOut()
    navigate('/login')
  }

  return (
    <header className="h-14 shrink-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex items-center px-4 gap-3 z-30">
      {/* Logo */}
      <div className="flex items-center gap-2.5 w-60 shrink-0">
        <CerahLogo />
        <div className="leading-none">
          <p className="text-cerah-navy dark:text-white font-bold text-sm">CERAH Pedia</p>
          <p className="text-gray-400 text-[10px] mt-0.5">Knowledge Management – CERAH</p>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex-1 max-w-xl">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            placeholder="Cari pengetahuan, dokumen, atau topik..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-cerah-teal/20 focus:border-cerah-teal dark:text-white placeholder:text-gray-400"
          />
        </div>
      </form>

      <div className="ml-auto flex items-center gap-1">
        {/* Theme toggle */}
        <button
          onClick={toggle}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors"
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => { setShowNotif(v => !v); setShowUser(false) }}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 relative transition-colors"
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold px-0.5">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {showNotif && (
            <div className="absolute right-0 top-11 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 z-50 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                <p className="text-sm font-semibold text-gray-800 dark:text-white">Notifikasi</p>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-xs text-cerah-teal hover:underline">
                    Tandai semua dibaca
                  </button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">Tidak ada notifikasi</p>
                ) : (
                  notifications.map(n => (
                    <button
                      key={n.id}
                      onClick={() => { markRead(n.id); if (n.link) navigate(n.link); setShowNotif(false) }}
                      className={cn(
                        'w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-50 dark:border-gray-700 transition-colors',
                        !n.read && 'bg-cerah-teal-light dark:bg-cerah-teal/10'
                      )}
                    >
                      <p className="text-xs font-medium text-gray-800 dark:text-white line-clamp-1">{n.title}</p>
                      {n.body && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{n.body}</p>}
                      <p className="text-[10px] text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* User */}
        <div className="relative ml-1" ref={userRef}>
          <button
            onClick={() => { setShowUser(v => !v); setShowNotif(false) }}
            className="w-8 h-8 rounded-full bg-cerah-teal text-white text-xs font-bold flex items-center justify-center shrink-0"
          >
            {initials}
          </button>

          {showUser && (
            <div className="absolute right-0 top-10 w-52 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 py-1 z-50 overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                <p className="text-xs text-gray-400 truncate">{user?.email}</p>
              </div>
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <LogOut size={14} /> Keluar
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

function CerahLogo() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" className="shrink-0">
      <circle cx="16" cy="16" r="16" fill="#0d7878" />
      <circle cx="16" cy="16" r="6" fill="white" fillOpacity="0.9" />
      {[0,45,90,135,180,225,270,315].map((deg, i) => (
        <line key={i} x1="16" y1="3" x2="16" y2="7"
          stroke="white" strokeWidth="2" strokeLinecap="round"
          transform={`rotate(${deg} 16 16)`} />
      ))}
    </svg>
  )
}

// Re-export icons for use in search results
export { FileText, Video, User }
